/** Thin client over the customer portal's `GET /api/v1/instances`. No React, no caching. */
import { authDispatcher } from '../authDispatcher';

/** Selector page size. Results are ranked before pagination, so page 1 holds the best matches. */
export const PORTAL_PAGE_SIZE = 20;
/**
 * How long to wait after a keystroke before searching the portal. Trailing edge, so a burst
 * of typing is one request — and short enough that the list still feels live.
 */
export const PORTAL_SEARCH_DEBOUNCE_MS = 250;
/** The `id` filter is comma-separated; one request covers at most a full portal page. */
export const PORTAL_MAX_IDS_PER_REQUEST = 100;
/** The portal's own cap on `size`. Used when walking the whole list, to spend few requests. */
export const PORTAL_MAX_PAGE_SIZE = 500;
/** Hard stop when walking every page, well past the ~1500 fleet. */
export const PORTAL_MAX_PAGES = 20;
/** The portal rejects a longer `search` with a 400 — trim client-side rather than provoke it. */
export const PORTAL_MAX_SEARCH_LENGTH = 200;

export type PortalInstance = {
  id?: string;
  name?: string;
  account?: string;
  product?: string;
  artescaInstanceId?: string;
};

export type PortalInstancesPage = {
  instances: PortalInstance[];
  totalCount: number;
  nextPage?: number;
};

/**
 * The portal host, or '' when it isn't explicitly configured.
 *
 * Deliberately has no default: the request carries the user's access token, so an
 * environment that turns the flag on without naming a portal must stay inert rather
 * than fall back to someone else's portal. Requiring an `https://` prefix guards both
 * an unset var in local dev and the `${CUSTOMER_PORTAL_API_URL}` placeholder left
 * unsubstituted by 40-envsubst-on-ui.sh — same shape check `sentry.ts` uses on its DSN.
 */
export const portalApiUrl = (): string => {
  const url = process.env.REACT_APP_CUSTOMER_PORTAL_API_URL ?? '';
  if (!url.startsWith('https://')) return '';
  // The portal grants CORS to registered origins only, so an instance on its own
  // port reaches it through the dev server rather than directly.
  return authDispatcher ? `${window.location.origin}/portal` : url;
};

const LEADING_SEPARATORS = /^[\s\-–—_:/|,.()[\]]+/;
const WORD = /[a-z0-9]+/gi;

/** Each word of `text`, with the offset just past it. */
const wordsOf = (text: string): { word: string; end: number }[] => {
  const words: { word: string; end: number }[] = [];
  const scan = new RegExp(WORD);
  for (let match = scan.exec(text); match; match = scan.exec(text)) {
    words.push({ word: match[0].toLowerCase(), end: match.index + match[0].length });
  }
  return words;
};

/**
 * Word by word, not literally: the two fields punctuate the same account differently
 * (`Acme Corp (Northwind)` vs `Acme Corp Northwind Paris-DATA`). An acronym is not a match.
 */
const opensWithAccount = (name: string, account: string): boolean => {
  const accountWords = wordsOf(account);
  const nameWords = wordsOf(name);
  if (accountWords.length === 0 || nameWords.length < accountWords.length) return false;
  return accountWords.every((entry, index) => nameWords[index]?.word === entry.word);
};

/**
 * The name with its account prefix dropped, for a list already scoped to one customer.
 * A name that is its account and nothing else is kept whole — there is no shorter one.
 */
export const instanceOwnName = (instance: PortalInstance): string => {
  const name = instance.name?.trim() ?? '';
  const account = instance.account?.trim();
  if (!name || !account || !opensWithAccount(name, account)) return name;

  const accountWords = wordsOf(account);
  const nameWords = wordsOf(name);
  if (nameWords.length === accountWords.length) return name;

  return (
    name
      .slice(nameWords[accountWords.length - 1]?.end)
      .replace(LEADING_SEPARATORS, '')
      .trim() || name
  );
};

/**
 * `account - name`, prepending the account only where the name does not already carry it:
 * the portal writes it in for some customers, and a blind join reads `Blue Harbor - Blue
 * Harbor - Blue Cloud …`.
 */
export const formatPortalInstanceName = (instance: PortalInstance): string => {
  const name = instance.name?.trim();
  const account = instance.account?.trim();
  if (!name) return account ?? '';
  if (!account) return name;
  return opensWithAccount(name, account) ? name : `${account} - ${name}`;
};

type PortalInstancesQuery = {
  baseUrl: string;
  ids?: string[];
  artescaInstanceIds?: string[];
  /** Product families to keep, e.g. `['ARTESCA']`. Empty or absent means every product. */
  products?: string[];
  search?: string;
  page?: number;
  size?: number;
};

export const buildPortalInstancesUrl = ({
  baseUrl,
  ids,
  artescaInstanceIds,
  products,
  search,
  page,
  size,
}: PortalInstancesQuery): string => {
  const params = new URLSearchParams();
  if (ids?.length) params.set('id', ids.join(','));
  if (artescaInstanceIds?.length) params.set('artescaInstanceId', artescaInstanceIds.join(','));
  // `product=` would be an empty value, not "no filter".
  if (products?.length) params.set('product', products.join(','));

  // Unlike every other filter, `search` is NOT comma-split by the portal: commas are
  // legitimate inside company names, so `Acme, Inc` must stay one literal query.
  const trimmedSearch = (search ?? '').trim();
  if (trimmedSearch) params.set('search', trimmedSearch.slice(0, PORTAL_MAX_SEARCH_LENGTH));

  if (page) params.set('page', String(page));
  if (size) params.set('size', String(size));

  return `${baseUrl}/api/v1/instances?${params.toString()}`;
};

export const fetchPortalInstancesPage = async ({
  token,
  ...query
}: PortalInstancesQuery & { token: string }): Promise<PortalInstancesPage> => {
  const response = await fetch(buildPortalInstancesUrl(query), {
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!response.ok) {
    throw new Error(`Customer portal instances request failed with status ${response.status}`);
  }
  const body = (await response.json()) as Partial<PortalInstancesPage>;
  return {
    instances: body.instances ?? [],
    totalCount: body.totalCount ?? 0,
    nextPage: body.nextPage,
  };
};

/**
 * Every instance the caller can reach, optionally narrowed to some products.
 *
 * Complete, not one page: the customer list is a group-by over the whole set, so a partial fetch
 * would silently hide customers. Sequential, the portal reporting `nextPage` and not a count.
 */
export const fetchAllPortalInstances = async ({
  baseUrl,
  token,
  products,
}: {
  baseUrl: string;
  token: string;
  products?: string[];
}): Promise<PortalInstance[]> => {
  const instances: PortalInstance[] = [];
  let page: number | undefined = 1;

  for (let requested = 0; page !== undefined && requested < PORTAL_MAX_PAGES; requested += 1) {
    const result: PortalInstancesPage = await fetchPortalInstancesPage({
      baseUrl,
      token,
      products,
      page,
      size: PORTAL_MAX_PAGE_SIZE,
    });
    instances.push(...result.instances);
    const advances: boolean = result.nextPage !== undefined && result.nextPage > page;
    page = advances ? result.nextPage : undefined;
  }

  return instances;
};

const chunk = <T>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

/**
 * Resolve a set of ids. `size` is pinned to the chunk size: with the portal's default
 * of 100 a full 100-id chunk would sit exactly on the page boundary, and any future
 * change to that default would silently start dropping the tail of each chunk.
 */
export const fetchPortalInstancesByIds = async ({
  baseUrl,
  token,
  ids,
}: {
  baseUrl: string;
  token: string;
  ids: string[];
}): Promise<PortalInstance[]> => {
  const pages = await Promise.all(
    chunk(ids, PORTAL_MAX_IDS_PER_REQUEST).map((batch) =>
      fetchPortalInstancesPage({ baseUrl, token, ids: batch, size: PORTAL_MAX_IDS_PER_REQUEST }),
    ),
  );
  return pages.flatMap((page) => page.instances);
};
